import React, { useCallback, useState } from 'react';

import Button, { LoadingButton } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import SectionMessage from '@atlaskit/section-message';
import { fixCurrentMappings, removeCurrentMapping, useMyUserMapping } from 'src/services/myUserMapping';

import jira from 'src/lib/api/jira';
import { locationReload } from 'src/lib/history';

import CenteredSpinner from 'src/views/components/CenteredSpinner';
import ErrorMessage from 'src/views/components/ErrorMessage';
import ModalButtons from 'src/views/components/ModalButtons';
import ModalContainer from 'src/views/components/ModalContainer';
import UserMappingSummary from 'src/views/components/UserMappingSummary';

const MyUserMappingView = ({ loading,
  loadingError,
  warningMessages,
  mappings,
  mappingBasedOnSessionDetails }) => {
  if (loadingError) return <ErrorMessage error={['Failed to validate user accounts mapping', loadingError]}/>;
  if (loading) return <CenteredSpinner size="large"/>;

  if (warningMessages.length === 0 && mappings.length === 0) {
    return <ErrorMessage error={['User accounts mapping is not supported. Ask your Jira administrator to connect Jira to PagerDuty account first.']}/>;
  }

  if (warningMessages.length === 0) {
    return (
      <div>
        <UserMappingSummary userMapping={mappings[0]}/>
      </div>
    );
  } else {
    return (
      <div>
        <SectionMessage appearance="warning">
          <ul>
            {warningMessages.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </SectionMessage>
        <div style={{ width: '50%', margin: '20px auto' }}>
          <Heading size="medium">Accounts you are logged in</Heading>
          <UserMappingSummary userMapping={mappingBasedOnSessionDetails}/>
          <div style={{ height: '20px' }}/>
          {
            mappings.length > 0 && <div>
              <Heading size="medium">Existing accounts mappings</Heading>
              {
                mappings.map(m => <UserMappingSummary userMapping={m} key={m.id}/>)
              }
            </div>
          }
        </div>
      </div>
    );
  }
};

const ModalLinkUsers = () => {
  const userMapping = useMyUserMapping();
  const { loading, loadingError, warningMessages, mappings } = userMapping;

  const [{ submitting, submitError }, setState] = useState({});
  const allowAny = !loading && !loadingError;

  const unlink = allowAny && warningMessages.length === 0 && mappings.length === 1;
  const link = allowAny && mappings.length === 0 && warningMessages.length > 0;
  const fix = allowAny && (mappings.length >= 1 && warningMessages.length > 0);

  const createMappings = useCallback((e) => {
    setState({ submitting: true });
    fixCurrentMappings()
      .then(() => jira.closeCurrentModal())
      .catch(e => setState({ submitError: e }));
  }, []);

  const removeMapping = useCallback(() => {
    setState({ submitting: true });
    removeCurrentMapping().then(() => locationReload())
      .catch(e => setState({ submitError: e }));
  }, []);

  return (
    <ModalContainer title="User accounts mapping">
      <div style={{ minHeight: 240 }}>
        <MyUserMappingView {...userMapping}/>
        { submitError && <ErrorMessage error={['Failed to process request', submitError]}/> }
      </div>
      <ModalButtons>
        <Button onClick={() => jira.closeCurrentModal()}>Cancel</Button>
        { unlink && <LoadingButton appearance="danger" onClick={removeMapping} isLoading={submitting}>Unlink my accounts</LoadingButton> }
        { link && <LoadingButton appearance="primary" onClick={createMappings} isLoading={submitting}>Link my accounts</LoadingButton> }
        { fix && <LoadingButton appearance="primary" onClick={createMappings} isLoading={submitting}>Fix broken user mappings</LoadingButton> }
      </ModalButtons>
    </ModalContainer>
  );
};

export default ModalLinkUsers;
